import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import Sortable from "sortablejs"

export default class extends Controller {
  static targets = [
    "input",
    "preview",
    "message",
    "addButton",
    "counter",
    "removePhotoIds"
  ]

  static values = {
    maxCount: { type: Number, default: 5 },
    maxSize: { type: Number, default: 10 * 1024 * 1024 },
    existing: { type: Array, default: [] },
    paramScope: { type: String, default: "" },
    paramName: { type: String, default: "photos" },
    directUploadUrl: { type: String, default: "/rails/active_storage/direct_uploads" },
    companionCount: { type: Number, default: 0 },
    manageHidden: { type: Boolean, default: true }
  }

  connect() {
    this.existingPhotos = [ ...this.existingValue ]
    this.activeUploads = new Set()
    this.form = this.element.closest("form")
    this.initSortable()
    this.render()
    this.dispatch("connected")
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy()
    }
  }

  initSortable() {
    this.sortable = Sortable.create(this.previewTarget, {
      animation: 150,
      handle: ".photo-preview-handle",
      draggable: ".photo-preview-tile",
      filter: ".photo-preview-add",
      onMove: (event) => !event.related.classList.contains("photo-preview-add"),
      onEnd: () => this.reorderFromDom()
    })
  }

  select() {
    this.inputTarget.click()
  }

  changed() {
    const files = Array.from(this.inputTarget.files || [])
    this.inputTarget.value = ""

    const messages = []

    files.forEach((file) => {
      if (!this.validType(file)) {
        messages.push(`Файл ${file.name} не подходит: выберите jpeg, png или webp`)
        return
      }

      if (file.size > this.maxSizeValue) {
        messages.push(`Файл ${file.name} больше 10 МБ`)
        return
      }

      if (this.totalCount >= this.maxCountValue) {
        messages.push(`Не больше ${this.maxCountValue} фото`)
        return
      }

      this.uploadFile(file)
    })

    this.showMessages(messages)
  }

  validType(file) {
    return [ "image/jpeg", "image/png", "image/webp" ].includes(file.type)
  }

  uploadFile(file) {
    const previewEl = this.buildUploadingPreview(file)
    const token = this.generateToken()
    this.activeUploads.add(token)
    this.previewTarget.insertBefore(previewEl, this.addTile)
    this.updateCounter()
    this.updateAddButton()
    this.dispatchUploadEvent("upload:started", token)

    const delegate = {
      directUploadWillStoreFileWithXHR: (xhr) => this.trackUploadProgress(xhr, previewEl)
    }

    new DirectUpload(file, this.directUploadUrlValue, delegate).create((error, blob) => {
      previewEl.remove()
      this.activeUploads.delete(token)

      if (error) {
        this.showMessages([`Не удалось загрузить ${file.name}: ${error.message}`])
        this.render()
        this.dispatchUploadEvent("upload:finished", token, { error })
        return
      }

      const photo = { signed_id: blob.signed_id, url: URL.createObjectURL(file) }
      this.existingPhotos.push(photo)
      this.render()
      this.preheatBlob(blob.signed_id)
      this.dispatchUploadEvent("upload:finished", token)
    })
  }

  generateToken() {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`
  }

  dispatchUploadEvent(name, token, extra = {}) {
    this.form?.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      detail: { token, ...extra }
    }))
  }

  preheatBlob(signedId) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    if (!signedId || !token) return

    fetch("/blobs/preheat", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token
      },
      body: JSON.stringify({ signed_id: signedId })
    }).catch(() => {})
  }

  trackUploadProgress(xhr, previewEl) {
    const bar = previewEl.querySelector(".direct-upload-progress-bar")
    const text = previewEl.querySelector(".direct-upload-progress-text")
    if (!bar || !text) return

    xhr.upload.addEventListener("progress", (event) => {
      const percent = event.lengthComputable ? Math.round(event.loaded / event.total * 100) : 0
      bar.style.width = `${percent}%`
      text.textContent = `Загрузка файла: ${percent}%`
    })

    xhr.upload.addEventListener("loadend", () => {
      bar.style.width = "100%"
      text.textContent = "Загрузка файла: 100%"
    })
  }

  buildUploadingPreview(file) {
    const el = document.createElement("div")
    el.className = "relative aspect-square rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center"
    el.innerHTML = `
      <img src="${URL.createObjectURL(file)}" class="w-full h-full object-cover opacity-50">
      <div class="absolute inset-0 flex items-center justify-center">
        <svg class="animate-spin h-6 w-6 text-[var(--color-primary)]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
      <div class="absolute bottom-0 left-0 right-0 px-2 pb-2">
        <div class="h-1.5 bg-black/30 rounded-full overflow-hidden">
          <div class="direct-upload-progress-bar h-full bg-[var(--color-primary)] w-0 transition-all duration-200"></div>
        </div>
        <div class="direct-upload-progress-text mt-1 text-center text-[10px] text-white drop-shadow">Загрузка файла: 0%</div>
      </div>
    `
    return el
  }

  remove(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    const photo = this.existingPhotos[index]
    if (!photo) return

    this.existingPhotos.splice(index, 1)
    this.render()
  }

  reorderFromDom() {
    const orderedIds = Array.from(this.previewTarget.children)
      .map((el) => el.dataset.signedId)
      .filter(Boolean)

    const orderMap = new Map(orderedIds.map((id, index) => [ id, index ]))
    this.existingPhotos.sort((a, b) => orderMap.get(a.signed_id) - orderMap.get(b.signed_id))
    this.updateHiddenInputs()
  }

  addPhoto(photo) {
    this.existingPhotos.push(photo)
    this.render()
  }

  resetPhotos() {
    this.existingPhotos = []
    this.render()
  }

  render() {
    this.previewTarget.innerHTML = ""

    this.existingPhotos.forEach((photo, index) => {
      this.previewTarget.appendChild(this.buildPreview(photo.url, index, photo.signed_id))
    })

    this.previewTarget.appendChild(this.buildAddTile())
    this.updateCounter()
    this.updateAddButton()
    this.updateHiddenInputs()
    this.dispatch("changed")
  }

  buildPreview(url, index, signedId) {
    const el = document.createElement("div")
    el.className = "photo-preview-tile relative aspect-square rounded-lg overflow-hidden bg-gray-100 group"
    el.dataset.signedId = signedId
    el.innerHTML = `
      <img src="${url}" class="w-full h-full object-cover">
      <div class="photo-preview-handle absolute top-1 left-1 p-1 rounded bg-black/40 text-white cursor-grab active:cursor-grabbing touch-none" data-sortable-ignore="true">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="12" r="1"/><circle cx="9" cy="5" r="1"/><circle cx="9" cy="19" r="1"/><circle cx="15" cy="12" r="1"/><circle cx="15" cy="5" r="1"/><circle cx="15" cy="19" r="1"/></svg>
      </div>
      <button type="button" data-action="photo-preview#remove" data-index="${index}" class="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70">
        ×
      </button>
    `
    return el
  }

  buildAddTile() {
    const atLimit = this.totalCount >= this.maxCountValue
    const el = document.createElement("button")
    el.type = "button"
    el.dataset.action = "photo-preview#select"
    el.dataset.notDraggable = "true"
    el.className = `photo-preview-add aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 ${atLimit
      ? "border-gray-200 text-gray-300 cursor-not-allowed"
      : "border-gray-300 text-gray-400 hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition"}`
    el.disabled = atLimit
    el.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" x2="12" y1="5" y2="19"/><line x1="5" x2="19" y1="12" y2="12"/></svg>
      <span class="text-xs">Добавить</span>
    `
    return el
  }

  get addTile() {
    return this.previewTarget.querySelector(".photo-preview-add") || null
  }

  setCompanionCount(count) {
    this.companionCountValue = count
    this.updateCounter()
  }

  updateCounter() {
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${this.totalCount + this.companionCountValue}/${this.maxCountValue}`
    }
  }

  updateAddButton() {
    if (!this.hasAddButtonTarget) return

    if (this.totalCount >= this.maxCountValue) {
      this.addButtonTarget.disabled = true
      this.addButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
    } else {
      this.addButtonTarget.disabled = false
      this.addButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
    }
  }

  updateHiddenInputs() {
    if (!this.hasRemovePhotoIdsTarget) return
    if (!this.manageHiddenValue) {
      this.removePhotoIdsTarget.innerHTML = ""
      return
    }

    this.removePhotoIdsTarget.innerHTML = ""

    this.existingPhotos.forEach((photo) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = `${this.paramScopeValue}[${this.paramNameValue}][]`
      input.value = photo.signed_id
      input.dataset.signedId = photo.signed_id
      this.removePhotoIdsTarget.appendChild(input)
    })
  }

  showMessages(messages) {
    if (!this.hasMessageTarget) return

    if (messages.length === 0) {
      this.clearMessages()
      return
    }

    this.messageTarget.textContent = [ ...new Set(messages) ].join("; ")
    this.messageTarget.classList.remove("hidden")
  }

  clearMessages() {
    if (!this.hasMessageTarget) return

    this.messageTarget.textContent = ""
    this.messageTarget.classList.add("hidden")
  }

  get totalCount() {
    return this.existingPhotos.length + this.activeUploads.size
  }
}
